import React, { useState } from 'react';
import { 
  Briefcase, TrendingUp, DollarSign, PieChart, Bitcoin, Calendar, Wallet, 
  BookOpen, Lock, Info 
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import Sparkline, { generateSparklineData } from '../components/Sparkline';
import PaperTrading from '../components/PaperTrading';
import './PortfolioPage.css';

// Donut Chart Component with Hover Tooltip
function DonutChart({ data, totalValue, centerLabel, formatPrice }) {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  
  const size = 160;
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const centerX = size / 2;
  const centerY = size / 2;

  let currentOffset = 0;
  const segments = data.map((item, index) => {
    const percentage = (item.value / totalValue) * 100;
    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
    const strokeDashoffset = -currentOffset;
    currentOffset += (percentage / 100) * circumference;
    return { ...item, percentage, strokeDasharray, strokeDashoffset };
  });

  const formattedTotal = formatPrice ? formatPrice(totalValue) : `$${(totalValue / 1000).toFixed(1)}K`;
  const displayTotal = totalValue >= 1000 ? formattedTotal.replace(/,\d{3}\.\d{2}$/, 'K') : formattedTotal;

  return (
    <div className="donut-chart-wrapper">
      <div className="donut-chart-container" onMouseLeave={() => setHoveredSegment(null)}>
        <svg width={size} height={size} className="donut-chart">
          <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={strokeWidth} />
          {segments.map((segment, index) => (
            <circle
              key={segment.symbol}
              cx={centerX} cy={centerY} r={radius}
              fill="none" stroke={segment.color} strokeWidth={strokeWidth}
              strokeDasharray={segment.strokeDasharray}
              strokeDashoffset={segment.strokeDashoffset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${centerX} ${centerY})`}
              className={`donut-segment ${hoveredSegment === segment.symbol ? 'hovered' : ''} ${hoveredSegment && hoveredSegment !== segment.symbol ? 'dimmed' : ''}`}
              onMouseEnter={() => setHoveredSegment(segment.symbol)}
              style={{ cursor: 'pointer' }}
            />
          ))}
          <text x={centerX} y={centerY - 8} textAnchor="middle" className="donut-center-label">{centerLabel || 'Total Value'}</text>
          <text x={centerX} y={centerY + 12} textAnchor="middle" className="donut-center-value">{displayTotal}</text>
        </svg>
        
        {hoveredSegment && (
          <div className="donut-tooltip">
            <div className="tooltip-header">
              <span className="tooltip-color" style={{ backgroundColor: segments.find(s => s.symbol === hoveredSegment)?.color }}></span>
              <span className="tooltip-title">{hoveredSegment}</span>
            </div>
            <div className="tooltip-value">
              {formatPrice ? formatPrice(segments.find(s => s.symbol === hoveredSegment)?.value) : `$${segments.find(s => s.symbol === hoveredSegment)?.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            </div>
            <div className="tooltip-percent">
              {segments.find(s => s.symbol === hoveredSegment)?.percentage.toFixed(1)}% of {centerLabel}
            </div>
          </div>
        )}
      </div>
      
      <div className="donut-legend-compact">
        {segments.map((segment) => (
          <div 
            key={segment.symbol} 
            className={`legend-dot-item ${hoveredSegment === segment.symbol ? 'active' : ''}`}
            onMouseEnter={() => setHoveredSegment(segment.symbol)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            <span className="legend-color" style={{ backgroundColor: segment.color }}></span>
            <span className="legend-label">{segment.symbol}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Performance Chart Component
function PerformanceChart({ data, formatPrice }) {
  const width = 600;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const values = data.map(d => d.value);
  const min = Math.min(...values) * 0.98;
  const max = Math.max(...values) * 1.02;
  const range = max - min;

  const points = data.map((d, i) => {
    const x = padding.left + (i / (data.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((d.value - min) / range) * chartHeight;
    return { x, y, ...d };
  });

  const pathD = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
  const areaD = `${pathD} L ${points[points.length - 1].x},${padding.top + chartHeight} L ${padding.left},${padding.top + chartHeight} Z`;
  const isPositive = values[values.length - 1] >= values[0];
  const color = isPositive ? '#10b981' : '#ef4444';

  const yLabels = [min, (min + max) / 2, max].map(v => ({
    value: v,
    y: padding.top + chartHeight - ((v - min) / range) * chartHeight
  }));

  const formatYLabel = (value) => {
    if (formatPrice) {
      const formatted = formatPrice(value);
      if (value >= 1000) return formatted.split(',')[0] + 'K';
      return formatted;
    }
    return `$${(value / 1000).toFixed(1)}K`;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="portfolio-chart">
      <defs>
        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {yLabels.map((label, i) => (
        <line key={i} x1={padding.left} y1={label.y} x2={width - padding.right} y2={label.y} stroke="rgba(255,255,255,0.1)" strokeDasharray="4" />
      ))}
      {yLabels.map((label, i) => (
        <text key={i} x={padding.left - 10} y={label.y + 4} textAnchor="end" className="chart-label">{formatYLabel(label.value)}</text>
      ))}
      {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0 || i === data.length - 1).map((d, i) => {
        const idx = data.indexOf(d);
        const x = padding.left + (idx / (data.length - 1)) * chartWidth;
        return <text key={i} x={x} y={height - 8} textAnchor="middle" className="chart-label">{d.label}</text>;
      })}
      <path d={areaD} fill="url(#chartGradient)" />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="5" fill={color} className="current-dot" />
    </svg>
  );
}

function PortfolioPage() {
  const { t, formatPrice, preferences } = useUserPreferences();
  
  // TOP-LEVEL MODE: 'live' or 'paper'
  const [portfolioMode, setPortfolioMode] = useState('paper');
  const [activeTab, setActiveTab] = useState('portfolio');
  const [timeframe, setTimeframe] = useState('1M');

  // Portfolio Holdings with GICS Sectors
  const holdings = [
    { symbol: 'AAPL', name: 'Apple Inc.', sector: 'Technology', shares: 50, avgCost: 165.00, currentPrice: 191.24, value: 9562.00, gain: 1312.00, gainPercent: 15.90, color: '#3b82f6', dividend: 0.96, divYield: 0.50 },
    { symbol: 'NVDA', name: 'NVIDIA Corp', sector: 'Technology', shares: 25, avgCost: 120.00, currentPrice: 142.50, value: 3562.50, gain: 562.50, gainPercent: 18.75, color: '#3b82f6', dividend: 0.16, divYield: 0.03 },
    { symbol: 'MSFT', name: 'Microsoft', sector: 'Technology', shares: 15, avgCost: 380.00, currentPrice: 432.15, value: 6482.25, gain: 782.25, gainPercent: 13.72, color: '#3b82f6', dividend: 3.00, divYield: 0.69 },
    { symbol: 'GOOGL', name: 'Alphabet Inc', sector: 'Communication Services', shares: 30, avgCost: 155.00, currentPrice: 175.50, value: 5265.00, gain: 615.00, gainPercent: 13.23, color: '#6366f1', dividend: 0.80, divYield: 0.46 },
    { symbol: 'TSLA', name: 'Tesla Inc', sector: 'Consumer Discretionary', shares: 10, avgCost: 280.00, currentPrice: 352.80, value: 3528.00, gain: 728.00, gainPercent: 26.00, color: '#f59e0b', dividend: 0, divYield: 0 },
    { symbol: 'JPM', name: 'JPMorgan Chase', sector: 'Financials', shares: 20, avgCost: 150.00, currentPrice: 198.50, value: 3970.00, gain: 970.00, gainPercent: 32.33, color: '#22c55e', dividend: 4.60, divYield: 2.32 },
    { symbol: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', shares: 15, avgCost: 160.00, currentPrice: 154.20, value: 2313.00, gain: -87.00, gainPercent: -3.63, color: '#06b6d4', dividend: 4.96, divYield: 3.22 },
    { symbol: 'XOM', name: 'Exxon Mobil', sector: 'Energy', shares: 25, avgCost: 95.00, currentPrice: 108.75, value: 2718.75, gain: 343.75, gainPercent: 14.47, color: '#ef4444', dividend: 3.80, divYield: 3.49 },
    { symbol: 'PG', name: 'Procter & Gamble', sector: 'Consumer Staples', shares: 12, avgCost: 145.00, currentPrice: 168.50, value: 2022.00, gain: 282.00, gainPercent: 16.21, color: '#eab308', dividend: 4.03, divYield: 2.39 },
    { symbol: 'CAT', name: 'Caterpillar Inc', sector: 'Industrials', shares: 8, avgCost: 280.00, currentPrice: 365.20, value: 2921.60, gain: 681.60, gainPercent: 30.43, color: '#8b5cf6', dividend: 5.20, divYield: 1.42 },
  ];

  const sectorColors = {
    'Technology': '#3b82f6', 'Healthcare': '#14b8a6', 'Financials': '#22c55e',
    'Consumer Discretionary': '#f97316', 'Consumer Staples': '#eab308', 'Energy': '#ef4444',
    'Industrials': '#a855f7', 'Materials': '#78716c', 'Utilities': '#6b7280',
    'Real Estate': '#ec4899', 'Communication Services': '#f43f5e',
  };

  const cryptoHoldings = [
    { symbol: 'BTC', name: 'Bitcoin', amount: 0.5, avgCost: 45000, currentPrice: 101250, value: 50625, gain: 28125, gainPercent: 125.0, color: '#f59e0b' },
    { symbol: 'ETH', name: 'Ethereum', amount: 5, avgCost: 2500, currentPrice: 3845.20, value: 19226, gain: 6726, gainPercent: 53.81, color: '#8b5cf6' },
    { symbol: 'SOL', name: 'Solana', amount: 50, avgCost: 100, currentPrice: 224.50, value: 11225, gain: 6225, gainPercent: 124.50, color: '#06b6d4' },
    { symbol: 'XRP', name: 'Ripple', amount: 1000, avgCost: 0.80, currentPrice: 2.34, value: 2340, gain: 1540, gainPercent: 192.50, color: '#64748b' },
  ];

  const marketIndices = [
    { symbol: 'SPY', name: 'S&P 500 ETF', shares: 20, avgCost: 480, currentPrice: 604.71, value: 12094.20, gain: 2494.20, gainPercent: 25.96, color: '#3b82f6' },
    { symbol: 'QQQ', name: 'NASDAQ 100 ETF', shares: 15, avgCost: 400, currentPrice: 525.80, value: 7887, gain: 1887, gainPercent: 31.45, color: '#22c55e' },
    { symbol: 'DIA', name: 'Dow Jones ETF', shares: 10, avgCost: 380, currentPrice: 442.85, value: 4428.50, gain: 628.50, gainPercent: 16.54, color: '#f59e0b' },
    { symbol: 'IWM', name: 'Russell 2000 ETF', shares: 25, avgCost: 200, currentPrice: 238.45, value: 5961.25, gain: 961.25, gainPercent: 19.22, color: '#f43f5e' },
  ];

  const dividendHistory = [
    { date: '2024-12-15', symbol: 'AAPL', amount: 48.00, shares: 50 },
    { date: '2024-12-10', symbol: 'MSFT', amount: 45.00, shares: 15 },
    { date: '2024-11-15', symbol: 'AAPL', amount: 48.00, shares: 50 },
    { date: '2024-11-08', symbol: 'GOOGL', amount: 24.00, shares: 30 },
    { date: '2024-10-15', symbol: 'MSFT', amount: 45.00, shares: 15 },
    { date: '2024-10-12', symbol: 'NVDA', amount: 4.00, shares: 25 },
    { date: '2024-09-15', symbol: 'AAPL', amount: 48.00, shares: 50 },
    { date: '2024-09-10', symbol: 'MSFT', amount: 45.00, shares: 15 },
  ];

  // Calculations
  const totalValue = holdings.reduce((sum, h) => sum + h.value, 0);
  const totalGain = holdings.reduce((sum, h) => sum + h.gain, 0);
  const totalCost = holdings.reduce((sum, h) => sum + (h.avgCost * h.shares), 0);
  const totalCryptoValue = cryptoHoldings.reduce((sum, h) => sum + h.value, 0);
  const totalCryptoGain = cryptoHoldings.reduce((sum, h) => sum + h.gain, 0);
  const totalCryptoCost = cryptoHoldings.reduce((sum, h) => sum + (h.avgCost * h.amount), 0);
  const totalCryptoGainPercent = ((totalCryptoValue - totalCryptoCost) / totalCryptoCost * 100);
  const totalMarketValue = marketIndices.reduce((sum, h) => sum + h.value, 0);
  const totalMarketGain = marketIndices.reduce((sum, h) => sum + h.gain, 0);
  const totalMarketCost = marketIndices.reduce((sum, h) => sum + (h.avgCost * h.shares), 0);
  const totalDividends = dividendHistory.reduce((sum, d) => sum + d.amount, 0);
  const annualDividends = holdings.reduce((sum, h) => sum + (h.dividend * h.shares * 4), 0);
  const grandTotalValue = totalValue + totalCryptoValue + totalMarketValue;
  const grandTotalGain = totalGain + totalCryptoGain + totalMarketGain;
  const grandTotalCost = totalCost + totalCryptoCost + totalMarketCost;
  const grandTotalGainPercent = ((grandTotalValue - grandTotalCost) / grandTotalCost * 100);
  const totalHoldingsCount = holdings.length + cryptoHoldings.length + marketIndices.length;

  // Sector allocation
  const sectorTotals = {};
  holdings.forEach(h => {
    if (!sectorTotals[h.sector]) sectorTotals[h.sector] = { value: 0, color: sectorColors[h.sector] || '#64748b' };
    sectorTotals[h.sector].value += h.value;
  });

  const sectorArray = Object.entries(sectorTotals)
    .map(([sector, data]) => ({ symbol: sector, name: sector, value: data.value, color: data.color, percent: (data.value / totalValue) * 100 }))
    .sort((a, b) => b.value - a.value);
  
  const sortedSmallestFirst = [...sectorArray].sort((a, b) => a.value - b.value);
  let otherTotal = 0, otherPercent = 0;
  const sectorsInOther = new Set();
  const maxOtherPercent = sectorArray.filter(s => s.percent < 5).length > 4 ? 20 : 15;
  
  for (const sector of sortedSmallestFirst) {
    const newOtherPercent = otherPercent + sector.percent;
    if (newOtherPercent <= maxOtherPercent && sector.percent < 10) {
      sectorsInOther.add(sector.symbol);
      otherTotal += sector.value;
      otherPercent = newOtherPercent;
    }
  }
  
  const stockAllocation = [
    ...sectorArray.filter(s => !sectorsInOther.has(s.symbol)),
    ...(otherTotal > 0 ? [{ symbol: t('other') || 'Other', name: `${t('other') || 'Other'} (${sectorsInOther.size})`, value: otherTotal, color: '#64748b', percent: otherPercent }] : [])
  ];

  const etfAllocation = marketIndices.map(etf => ({ symbol: etf.symbol, name: etf.name, value: etf.value, color: etf.color }));
  const cryptoAllocation = cryptoHoldings.map(c => ({ symbol: c.symbol, name: c.name, value: c.value, color: c.color }));

  const generatePerformanceData = (baseValue) => {
    const data = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let value = baseValue * 0.8;
    for (let i = 0; i < 12; i++) {
      value += (Math.random() - 0.3) * (baseValue * 0.05);
      data.push({ label: months[i], value: Math.max(value, baseValue * 0.7) });
    }
    data[data.length - 1].value = baseValue;
    return data;
  };

  const liveTabs = [
    { id: 'portfolio', label: t('portfolio'), icon: <Briefcase size={18} /> },
    { id: 'crypto', label: t('crypto'), icon: <Bitcoin size={18} /> },
    { id: 'markets', label: t('markets'), icon: <TrendingUp size={18} /> },
    { id: 'dividends', label: t('dividends'), icon: <Calendar size={18} /> },
  ];

  const timeframes = ['1W', '1M', '3M', '6M', '1Y', 'ALL'];
  const formatCryptoPrice = (price) => formatPrice(price);

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <h1>{t('portfolio')}</h1>
        <p className="portfolio-subtitle">{t('trackPortfolio')}</p>
      </div>

      {/* TOP-LEVEL MODE SELECTOR */}
      <div className="portfolio-mode-selector">
        <button className="mode-btn disabled" disabled>
          <TrendingUp size={18} />
          {t('livePortfolio') || 'Live Portfolio'}
          <span className="mode-badge coming-soon"><Lock size={10} />{t('comingSoon') || 'Coming Soon'}</span>
        </button>
        <button className={`mode-btn ${portfolioMode === 'paper' ? 'active' : ''}`} onClick={() => setPortfolioMode('paper')}>
          <BookOpen size={18} />
          {t('paperTrading') || 'Paper Trading'}
          <span className="mode-badge practice">{t('practice') || 'Practice'}</span>
        </button>
      </div>

      {/* PAPER TRADING MODE */}
      {portfolioMode === 'paper' && (
        <>
          <div className="portfolio-info-banner">
            <Info size={16} />
            <span>Practice trading with virtual money</span>
          </div>
          <PaperTrading />
        </>
      )}

      {/* LIVE PORTFOLIO MODE */}
      {portfolioMode === 'live' && (
        <div className="live-portfolio-container">
          <div className="coming-soon-overlay">
            <div className="coming-soon-content">
              <Lock size={48} />
              <h2>{t('brokerIntegrationComingSoon') || 'Broker Integration Coming Soon'}</h2>
              <p>{t('brokerIntegrationDescription') || 'Connect your brokerage account to track real investments. We\'re working with partners to bring this feature to you.'}</p>
              <button className="try-paper-btn" onClick={() => setPortfolioMode('paper')}>
                <BookOpen size={18} />
                {t('tryPaperTrading') || 'Try Paper Trading Instead'}
              </button>
            </div>
          </div>

          <div className="live-portfolio-content blurred">
            <div className="portfolio-tabs">
              {liveTabs.map((tab) => (
                <button key={tab.id} className={`portfolio-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'portfolio' && (
              <>
                <div className="portfolio-stats">
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><Briefcase size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('totalValue')}</span>
                      <span className="stat-card-value">{formatPrice(grandTotalValue)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon positive"><TrendingUp size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('totalGain')}</span>
                      <span className="stat-card-value positive">+{formatPrice(grandTotalGain)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon positive"><DollarSign size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('return')}</span>
                      <span className="stat-card-value positive">+{grandTotalGainPercent.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><PieChart size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('totalHoldings')}</span>
                      <span className="stat-card-value">{totalHoldingsCount}</span>
                    </div>
                  </div>
                </div>

                <div className="portfolio-charts-grid">
                  <div className="portfolio-card allocation-card">
                    <div className="card-header"><h3>{t('assetAllocation')}</h3></div>
                    <div className="stacked-donut-container">
                      <div className="donut-section">
                        <h4>{t('stocksBySector')}</h4>
                        <DonutChart data={stockAllocation} totalValue={totalValue} centerLabel={t('stocks') || 'Stocks'} formatPrice={formatPrice} />
                      </div>
                      <div className="donut-divider"></div>
                      <div className="donut-section">
                        <h4>{t('crypto')}</h4>
                        <DonutChart data={cryptoAllocation} totalValue={totalCryptoValue} centerLabel={t('crypto')} formatPrice={formatPrice} />
                      </div>
                    </div>
                  </div>
                  <div className="portfolio-card performance-card">
                    <div className="card-header">
                      <h3>{t('performance')}</h3>
                      <div className="timeframe-tabs">
                        {timeframes.map((tf) => (
                          <button key={tf} className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`} onClick={() => setTimeframe(tf)}>{tf}</button>
                        ))}
                      </div>
                    </div>
                    <div className="performance-chart-container">
                      <div className="performance-summary">
                        <span className="performance-value">{formatPrice(grandTotalValue)}</span>
                        <span className="performance-change positive">+{formatPrice(grandTotalGain)} (+{grandTotalGainPercent.toFixed(2)}%)</span>
                      </div>
                      <PerformanceChart data={generatePerformanceData(grandTotalValue)} formatPrice={formatPrice} />
                    </div>
                  </div>
                </div>

                <div className="holdings-section">
                  <h2>{t('stocks') || 'Stock'} {t('holdings') || 'Holdings'}</h2>
                  <div className="holdings-table-container">
                    <table className="holdings-table">
                      <thead>
                        <tr>
                          <th>SYMBOL</th><th>{t('sector') || 'SECTOR'}</th><th>{t('shares') || 'SHARES'}</th>
                          <th>{t('avgCost') || 'AVG COST'}</th><th>{t('price').toUpperCase()}</th>
                          <th>{t('value') || 'VALUE'}</th><th>{t('gainLoss') || 'GAIN/LOSS'}</th><th>{t('return')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdings.map((h) => (
                          <tr key={h.symbol}>
                            <td className="holding-symbol-cell">
                              <span className="holding-color" style={{ backgroundColor: sectorColors[h.sector] }}></span>
                              <div className="holding-info"><span className="holding-symbol">{h.symbol}</span><span className="holding-name">{h.name}</span></div>
                            </td>
                            <td className="sector-cell">{h.sector}</td>
                            <td>{h.shares}</td>
                            <td>{formatPrice(h.avgCost)}</td>
                            <td>{formatPrice(h.currentPrice)}</td>
                            <td className="holding-value">{formatPrice(h.value)}</td>
                            <td className={`holding-gain ${h.gain >= 0 ? 'positive' : 'negative'}`}>{h.gain >= 0 ? '+' : ''}{formatPrice(h.gain)}</td>
                            <td><span className={`holding-percent ${h.gainPercent >= 0 ? 'positive' : 'negative'}`}>{h.gainPercent >= 0 ? '+' : ''}{h.gainPercent.toFixed(2)}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'crypto' && (
              <>
                <div className="portfolio-stats">
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><Bitcoin size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('crypto')} {t('value') || 'Value'}</span>
                      <span className="stat-card-value">{formatPrice(totalCryptoValue)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon positive"><TrendingUp size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('totalGain')}</span>
                      <span className="stat-card-value positive">+{formatPrice(totalCryptoGain)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon positive"><DollarSign size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('return')}</span>
                      <span className="stat-card-value positive">+{totalCryptoGainPercent.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><Wallet size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('assets') || 'Assets'}</span>
                      <span className="stat-card-value">{cryptoHoldings.length}</span>
                    </div>
                  </div>
                </div>
                <div className="portfolio-charts-grid">
                  <div className="portfolio-card allocation-card">
                    <div className="card-header"><h3>{t('crypto')} {t('allocation') || 'Allocation'}</h3></div>
                    <DonutChart data={cryptoHoldings} totalValue={totalCryptoValue} centerLabel={t('crypto')} formatPrice={formatPrice} />
                  </div>
                  <div className="portfolio-card performance-card">
                    <div className="card-header">
                      <h3>{t('performance')}</h3>
                      <div className="timeframe-tabs">
                        {timeframes.map((tf) => (<button key={tf} className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`} onClick={() => setTimeframe(tf)}>{tf}</button>))}
                      </div>
                    </div>
                    <div className="performance-chart-container">
                      <div className="performance-summary">
                        <span className="performance-value">{formatPrice(totalCryptoValue)}</span>
                        <span className="performance-change positive">+{formatPrice(totalCryptoGain)} (+{totalCryptoGainPercent.toFixed(2)}%)</span>
                      </div>
                      <PerformanceChart data={generatePerformanceData(totalCryptoValue)} formatPrice={formatPrice} />
                    </div>
                  </div>
                </div>
                <div className="holdings-section">
                  <h2>{t('crypto')} {t('holdings') || 'Holdings'}</h2>
                  <div className="holdings-table-container">
                    <table className="holdings-table">
                      <thead><tr><th>{t('asset') || 'ASSET'}</th><th>{t('amount') || 'AMOUNT'}</th><th>{t('avgCost') || 'AVG COST'}</th><th>{t('price').toUpperCase()}</th><th>{t('value') || 'VALUE'}</th><th>{t('gainLoss') || 'GAIN/LOSS'}</th><th>{t('return')}</th></tr></thead>
                      <tbody>
                        {cryptoHoldings.map((c) => (
                          <tr key={c.symbol}>
                            <td className="holding-symbol-cell">
                              <span className="holding-color" style={{ backgroundColor: c.color }}></span>
                              <div className="holding-info"><span className="holding-symbol">{c.symbol}</span><span className="holding-name">{c.name}</span></div>
                            </td>
                            <td>{c.amount}</td><td>{formatCryptoPrice(c.avgCost)}</td><td>{formatCryptoPrice(c.currentPrice)}</td>
                            <td className="holding-value">{formatPrice(c.value)}</td>
                            <td className={`holding-gain ${c.gain >= 0 ? 'positive' : 'negative'}`}>{c.gain >= 0 ? '+' : ''}{formatPrice(c.gain)}</td>
                            <td><span className={`holding-percent ${c.gainPercent >= 0 ? 'positive' : 'negative'}`}>{c.gainPercent >= 0 ? '+' : ''}{c.gainPercent.toFixed(2)}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'markets' && (
              <div className="holdings-section">
                <h2>{t('markets')} ETF {t('holdings') || 'Holdings'}</h2>
                <div className="holdings-table-container">
                  <table className="holdings-table">
                    <thead><tr><th>SYMBOL</th><th>{t('shares') || 'SHARES'}</th><th>{t('avgCost') || 'AVG COST'}</th><th>{t('price').toUpperCase()}</th><th>{t('value') || 'VALUE'}</th><th>{t('gainLoss') || 'GAIN/LOSS'}</th><th>{t('return')}</th></tr></thead>
                    <tbody>
                      {marketIndices.map((etf) => (
                        <tr key={etf.symbol}>
                          <td className="holding-symbol-cell">
                            <span className="holding-color" style={{ backgroundColor: etf.color }}></span>
                            <div className="holding-info"><span className="holding-symbol">{etf.symbol}</span><span className="holding-name">{etf.name}</span></div>
                          </td>
                          <td>{etf.shares}</td><td>{formatPrice(etf.avgCost)}</td><td>{formatPrice(etf.currentPrice)}</td>
                          <td className="holding-value">{formatPrice(etf.value)}</td>
                          <td className={`holding-gain ${etf.gain >= 0 ? 'positive' : 'negative'}`}>{etf.gain >= 0 ? '+' : ''}{formatPrice(etf.gain)}</td>
                          <td><span className={`holding-percent ${etf.gainPercent >= 0 ? 'positive' : 'negative'}`}>{etf.gainPercent >= 0 ? '+' : ''}{etf.gainPercent.toFixed(2)}%</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'dividends' && (
              <>
                <div className="portfolio-stats">
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><DollarSign size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">YTD {t('dividends')}</span>
                      <span className="stat-card-value">{formatPrice(totalDividends)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon positive"><Calendar size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('estAnnual') || 'Est. Annual'}</span>
                      <span className="stat-card-value positive">{formatPrice(annualDividends)}</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><TrendingUp size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('avgYield') || 'Avg Yield'}</span>
                      <span className="stat-card-value">{((annualDividends / totalValue) * 100).toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="portfolio-stat-card">
                    <div className="stat-card-icon"><Briefcase size={22} /></div>
                    <div className="stat-card-content">
                      <span className="stat-card-label">{t('dividendStocks') || 'Dividend Stocks'}</span>
                      <span className="stat-card-value">{holdings.filter(h => h.dividend > 0).length}</span>
                    </div>
                  </div>
                </div>
                <div className="dividend-sections">
                  <div className="portfolio-card">
                    <div className="card-header"><h3>{t('recentDividendPayments') || 'Recent Dividend Payments'}</h3></div>
                    <div className="dividend-history">
                      {dividendHistory.map((div, index) => (
                        <div key={index} className="dividend-item">
                          <div className="dividend-info"><span className="dividend-symbol">{div.symbol}</span><span className="dividend-date">{div.date}</span></div>
                          <div className="dividend-amount positive">+{formatPrice(div.amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PortfolioPage;